<template>
  <div>
    <Toolset
      ref="toolset"
      :searcher="handleSearch"
      add-button-icon="pi pi-plus"
      add-button-label="Cargar cartas externas"
      :to-add="handleAdd"
      class="mt-5"
    >
    </Toolset>
    <DataTable
      class="tabla mb-5 mt-3"
      :value="letterConfigs.data"
      paginator
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      tableStyle="min-width: 50rem"
      removableSort
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      :totalRecords="letterConfigs.totalItems"
      :currentPageReportTemplate="currentPageReportTemplate"
      scrollable
      scrollHeight="600px"
      sortField="updatedAt"
      :sortOrder="-1"
    >
      <template #empty> No se encontraron cartas.</template>
      <Column field="name" header="Nombre" sortable></Column>
      <Column field="description" header="Descripción" sortable></Column>
      <Column field="generationType" header="Tipo de carta" sortable></Column>
      <Column field="payrolls" header="Nóminas" sortable>
        <template #body="{ data }">
          <span class="font-semibold text-overflow-elipsis">
            {{ payrollsToString(data.payrolls) }}
          </span>
        </template>
      </Column>
      <Column field="updatedAt" header="Última actualización" sortable>
        <template #body="{ data, field }">
          <span>{{ formatDateWithHour(data[field]) }}</span>
        </template>
      </Column>
      <Column header="Acciones">
        <template #body="{ data }">
          <ConfirmPopup></ConfirmPopup>
          <div class="actions">
            <Button
              v-tooltip.bottom="'Ver detalles'"
              class="action-btn view p-button-sm mr-2"
              icon="pi pi-eye"
              @click="handleViewDetails(data['id'], data['generationType'])"
            />
            <Button
              v-tooltip.bottom="'Eliminar'"
              class="action-btn view p-button-sm px-4 bg-red-500 border-red-500"
              @click="(event) => handleDelete(event, data['id'])"
            >
              <i class="pi pi-trash text-white"></i>
            </Button>
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import type List from '@/shared/dtos/List'
  import type LetterConfigList from '../models/LetterConfig'
  import type Payroll from '@/modules/payrolls-config/models/Payroll'

  interface ExternalLettersSectionProps {
    letterConfigs: List<LetterConfigList[]>
    currentPageReportTemplate: string
    payrollsToString: (payrolls: Payroll[]) => string
    formatDateWithHour: (value: any) => string
  }

  defineProps<ExternalLettersSectionProps>()

  const emit = defineEmits<{
    (e: 'search', value: string): void
    (e: 'add'): void
    (e: 'view', value: { id: string; generationType: string }): void
    (e: 'delete', value: { event: any; id: string }): void
  }>()

  const toolset = ref<{ search: any } | null>(null)

  const extractSearchValue = () => {
    if (!toolset.value) return ''
    const { search } = toolset.value
    if (typeof search === 'string') return search
    if (search && typeof search.value === 'string') return search.value
    return ''
  }

  const handleSearch = () => {
    emit('search', extractSearchValue())
  }

  const handleAdd = () => {
    emit('add')
  }

  const handleViewDetails = (id: string, generationType: string) => {
    emit('view', { id, generationType })
  }

  const handleDelete = (event: any, id: string) => {
    emit('delete', { event, id })
  }
</script>

<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
